import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Modal,
  Input, Row, Col, Divider, DatePicker, Card
} from 'antd';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';

import InfoView from 'components/common/InfoView';

import ButtonGroup from 'antd/lib/button/button-group';
import moment from 'moment/moment';
import {
  addMid,
  deleteProcessor,
  getProcessors,
  getProcessorsById,
  updateProcessor,
} from '../../redux/actions/Processors';
import { fetchTransactionProcessed } from '../../redux/actions/Transactions';
import { ProcessorsColumns } from '../../components/common/Columns';
import ProcessorForm from './ProcessorForm';
import generateQuery, { handleSearch } from '../../util/table';
import useForm from '../../hooks/useForm';
import { apiBaseUrl, dateAndTimeRangeQueriesArray } from '../../util/lib';
import { MIDS, PROCESSING_SUMMARIES } from '../../constants/resources';
import Can from '../../util/can';

const { RangePicker } = DatePicker;
const Processors = ({ merchantId }) => {
  const unicornApi = apiBaseUrl(process.env.REACT_APP_UNICORN_API);

  const dispatch = useDispatch();

  const { authUser } = useSelector(({ auth }) => auth);
  const isMerchant = authUser.role.name.includes('Merchant');

  const {
    data,
    loading,
    totalItems,
    pageSize,
    currentPage: current,
  } = useSelector(({ processors }) => processors.allProcessors);

  const { timezone } = useSelector(({ settings }) => settings);
  const todaysDate = moment().subtract('hours', timezone.offset).format('YYYY-MM-DD');

  const { transactionProcessed } = useSelector(({ transactions }) => transactions);

  const initialState = {
    begin_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
  };
  const [selectedTrafficTypes, setSelectedTrafficTypes] = useState('');
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [selectedCardTypes, setSelectedCardTypes] = useState('');
  const [selectedAllowedCountries, setSelectedAllowedCountries] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedMinimumAmount, setSelectedMinimumAmount] = useState(0);
  const [selectedMaximumAmount, setSelectedMaximumAmount] = useState(0);
  const [processorId, setProcessorId] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [processorName, setProcessorName] = useState('');
  const [visibleAcquirerReportModal, setVisibleAcquirerReportModal] = useState(false);
  const [dateRange, setSelectedDateRangeString] = useState(
    [initialState.begin_date, initialState.end_date],
  );
  const [dateRangeValue, setSelectedDateRangeValue] = useState(
    [moment(initialState.begin_date), moment(initialState.end_date)],
  );
  const {
    form,
    onFormOk,
    formVisibility,
    handleShowModal,
    handleCancel,
    handleEditRecord,
    editableRecord,
  } = useForm();


  useEffect(() => {
    const pagination = `page=${current}&limit=${pageSize}`;
    if (merchantId) {
      dispatch(getProcessorsById(merchantId, pagination));
    } else {
      dispatch(getProcessors(pagination));
    }

    const queries = [
      `merchant_id={"$in": ["${merchantId}"]}`,
      "transaction_currency=USD",
      ...dateAndTimeRangeQueriesArray(todaysDate, todaysDate, timezone),
    ];
    const params = queries.join('&');

    dispatch(fetchTransactionProcessed(params));
  }, [dispatch]);

  const editable = Object.keys(editableRecord).length !== 0;
  const colSize = 12;

  const handleDeleteProcessor = (id) => {
    dispatch(deleteProcessor(id));
  };

  const handleEditProcessor = (record) => {
    setSelectedTrafficTypes(record.traffic_types);
    setSelectedCardTypes(record.card_types);
    setSelectedAllowedCountries(record.allowed_countries || []);
    setSelectedMinimumAmount(record?.minimum_amount);
    setSelectedMaximumAmount(record?.maximum_amount);
    setSelectedServiceType(record.service_type);
    setSelectedCurrency(record.currency)
    setSelectedPaymentMethod(record.payment_method)
  };

  const handleCreateAcquirerReport = (record) => {
    setProcessorId(record._id);
    setProcessorName(record.name);
    setServiceType(record.service_type);
    setVisibleAcquirerReportModal(true);
  };

  const handleOnChange = (pagination, filters, sorter) => {
    dispatch(getProcessors(generateQuery(pagination, filters, sorter)));
  };

  const props = {
    loading,
    scroll: { x: 1300 },
    rowKey: '_id',
    // sticky: true,
    pagination: {
      current,
      pageSize,
      total: totalItems,
      showSizeChanger: false,
    },
    onChange: handleOnChange,
    columns: ProcessorsColumns(
      handleDeleteProcessor,
      handleEditProcessor,
      handleEditRecord,
      handleCreateAcquirerReport,
      isMerchant,
      transactionProcessed.data
    ),
    dataSource: data,
  };

  const handleOk = (values) => {
    const processorData = {
      ...values,
      traffic_types: selectedTrafficTypes,
      card_types: selectedCardTypes,
      allowed_countries: selectedAllowedCountries,
      service_type: selectedServiceType,
      currency: selectedCurrency,
      payment_method: selectedPaymentMethod,
      minimum_amount: Number(selectedMinimumAmount),
      maximum_amount: Number(selectedMaximumAmount),
      merchant: merchantId,
    };
    if (editable) {
      dispatch(updateProcessor(editableRecord._id, processorData));
    } else {
      dispatch(addMid(processorData));
    }
  };
  const handleChangeTrafficType = (value) => {
    setSelectedTrafficTypes(value);
  };

  const handleChangeCardType = (value) => {
    setSelectedCardTypes(value);
  };

  const handleChangeAllowedCountries = (value) => {
    setSelectedAllowedCountries(value);
  };

  const handleChangeCurrency = (value) => {
    setSelectedCurrency(value);
  };

  const handleChangePaymentMethod = (value) => {
    setSelectedPaymentMethod(value);
  };

  const handleChangeServiceType = (value) => {
    setSelectedServiceType(value);
  };

  const closeAcquirerReport = () => {
    setVisibleAcquirerReportModal(false);
  };

  const onDateRangeChanged = (value, dateString) => {
    setSelectedDateRangeString(dateString);
    setSelectedDateRangeValue(value);
  };

  const handleChange = (value, key) => {
    if (key === 'minimum_amount') {
      setSelectedMinimumAmount(value);
    } else if (key === 'maximum_amount') {
      setSelectedMaximumAmount(value);
    } else if (key === 'traffic_types') {
      setSelectedTrafficTypes(value);
    } else if (key === 'card_types') {
      setSelectedCardTypes(value);
    }
  };

  const handleCancelForm = () => {
    setSelectedTrafficTypes(null);
    setSelectedCardTypes(null);
    setSelectedAllowedCountries([]);
    setSelectedMinimumAmount(null);
    setSelectedMaximumAmount(null);
    setSelectedServiceType(null);
    setSelectedCurrency(null);
    setSelectedPaymentMethod(null);
  };

  return (
    (
      <div>
        <div className="gx-d-flex justify-content-center">
          <Can I="create" a={MIDS}>
            <Button
              icon={<PlusOutlined />}
              data-testid="add-processor-btn"
              type="primary"
              onClick={() => handleShowModal()}
            >
              Add MID/PSP
            </Button>
          </Can>
          <Input.Search
            data-testid="search-input"
            placeholder="Search mids"
            enterButton
            allowClear
            onSearch={(value) => handleSearch(
              value,
              dispatch,
              getProcessors,
              ProcessorsColumns,
            )}
          />
        </div>
        <div className="gx-d-flex justify-content-center">
          <Table data-testid="processor-table" className="gx-table-responsive" {...props} />
        </div>
        <Modal
          title={editable ? 'Edit Mid' : 'Add Mid'}
          open={formVisibility}
          okText={editable ? 'Save' : 'Add'}
          onOk={() => onFormOk(handleOk)}
          confirmLoading={loading}
          onCancel={() => {
            handleCancel();
            handleCancelForm();
          }}
          width={1200}
          destroyOnClose
          centered
        >
          <ProcessorForm {...{
            form,
            handleChangeTrafficType,
            selectedTrafficTypes,
            handleChangeCardType,
            selectedCardTypes,
            handleChangeAllowedCountries,
            selectedAllowedCountries,
            handleChangeServiceType,
            handleChangeCurrency,
            selectedCurrency,
            selectedPaymentMethod,
            handleChangePaymentMethod,
            selectedServiceType,
            minimumAmount: Number(selectedMinimumAmount),
            maximumAmount: Number(selectedMaximumAmount),
            handleChange,
            newRecord: !editable
          }}
          />
        </Modal>
        <Can I="read" a={PROCESSING_SUMMARIES}>
          <Modal
            title={`Mid Report - ${processorName}`}
            open={visibleAcquirerReportModal}
            onCancel={closeAcquirerReport}
            width="50%"
            footer={null}
            centered
            destroyOnClose
          >
            <>
              <div id="report-filters">
                <Row>
                  <Col xl={colSize} lg={colSize} md={colSize} sm={colSize} xs={24}>
                    <RangePicker
                      value={dateRangeValue}
                      onChange={(_, dateString) => onDateRangeChanged(_, dateString)}
                      style={{ width: '100%' }}
                    />
                  </Col>

                  <Col xl={colSize} lg={colSize} md={colSize} sm={colSize} xs={24}>
                    <ButtonGroup>
                      <Button
                        loading={loading}
                        key="submit"
                        className="gx-w-100 load-report-btn"
                        type="dashed"
                        icon={<DownloadOutlined />}
                        href={`${unicornApi}gateway-transaction-files/${serviceType}/${processorId}?processor_name=${processorName}&time_zone=${timezone.value}&begin_date=${dateRange[0]}&end_date=${dateRange[1]}`}
                      >
                        Download Report
                      </Button>
                    </ButtonGroup>
                  </Col>
                </Row>
                <Divider style={{ margin: '10px 0' }} />
              </div>
            </>
          </Modal>
        </Can>
        <InfoView />
      </div>
    )
  );
};

export default Processors;
